import restrictedZipcodesinState from "@/data/pfas-zipcode-state-restriction-list.json"
import { PFAS_COUNTRY } from "@/lib/siteconfig/constants"

export const stateNameToAbbr: Record<string, string> = {
    Alabama: "AL",
    Alaska: "AK",
    Arizona: "AZ",
    Arkansas: "AR",
    California: "CA",
    Colorado: "CO",
    Connecticut: "CT",
    "District of Columbia": "DC",
    Delaware: "DE",
    Florida: "FL",
    Georgia: "GA",
    Hawaii: "HI",
    Idaho: "ID",
    Illinois: "IL",
    Indiana: "IN",
    Iowa: "IA",
    Kansas: "KS",
    Kentucky: "KY",
    Louisiana: "LA",
    Maine: "ME",
    Maryland: "MD",
    Massachusetts: "MA",
    Michigan: "MI",
    Minnesota: "MN",
    Mississippi: "MS",
    Missouri: "MO",
    Montana: "MT",
    Nebraska: "NE",
    Nevada: "NV",
    "New Hampshire": "NH",
    "New Jersey": "NJ",
    "New Mexico": "NM",
    "New York": "NY",
    "North Carolina": "NC",
    "North Dakota": "ND",
    Ohio: "OH",
    Oklahoma: "OK",
    Oregon: "OR",
    Pennsylvania: "PA",
    "Rhode Island": "RI",
    "South Carolina": "SC",
    "South Dakota": "SD",
    Tennessee: "TN",
    Texas: "TX",
    Utah: "UT",
    Vermont: "VT",
    Virginia: "VA",
    Washington: "WA",
    "West Virginia": "WV",
    Wisconsin: "WI",
    Wyoming: "WY"
    // Add territories if you want
}

const zipSetByState = Object.fromEntries(
    Object.entries(restrictedZipcodesinState).map(([state, zips]) => [
        state,
        new Set(zips) // Set<string>
    ])
) as Record<string, Set<string>>

export const getStateAbbreviation = (state?: string): string => {
    if (!state) return ""
    return state.length > 2 ? stateNameToAbbr[state] : state.toUpperCase()
}

/**
 *
 * @param restrictedPfasStates restricted states from each basket item
 * @param customerShippingState customer shipping state
 * @param customerShippingZip customer shipping zip
 * @returns boolean
 */
export const isPfasShippingStateAndZipRestricted = (
    restrictedPfasStates?: { label: string; value: string }[],
    customerShippingState?: string,
    customerShippingZip?: string,
    country?: string
) => {
    if (
        !customerShippingState ||
        !customerShippingZip ||
        !restrictedPfasStates ||
        !country
    )
        return false

    if (country !== PFAS_COUNTRY) return false

    return restrictedPfasStates.some(state => {
        const stateAbbr = state.value.toUpperCase()
        const shippingStateAbbr = getStateAbbreviation(customerShippingState)
        return (
            stateAbbr === shippingStateAbbr &&
            restrictedZip(shippingStateAbbr, customerShippingZip)
        )
    })
}

/**
 * Returns true when the 5-digit ZIP is in the restricted list
 * for that state.  If the state isn’t present at all, it’s
 * treated as unrestricted. ZIP+4 is judged on the first five digits.
 */
export const restrictedZip = (state?: string, zip?: string): boolean => {
    if (!state || !zip) {
        return false
    }

    const abbr = getStateAbbreviation(state)

    if (!abbr) {
        return false
    }

    const paddedZip = zip.replace(/\D/g, "").slice(0, 5).padStart(5, "0")
    const stateZipSet = zipSetByState[abbr]

    if (!stateZipSet) {
        // No zipcode set yet from customer
        return false
    }

    const found = stateZipSet.has(paddedZip)

    return found
}
